import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  Alert,
  useMantineTheme,
  FocusTrap,
  PinInput,
  Center,
  ThemeIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck, IconPhoneCheck } from "@tabler/icons-react";
import EnglishLoadingWithCompletion from "./english-loading-with-completion";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";

const PhoneMatchForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [phoneMatchStep, setPhoneMatchStep] = useState(0); // 0: basic info, 1: additional fields, 2: OTP
  const [isOtpInput, setIsOtpInput] = useState(false);
  const [template, setTemplate] = useState({});
  const [hasHandledFailure, setHasHandledFailure] = useState(false);
  const inputSize = useAdjustInputSize();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [processingVerification, setProcessingVerification] = useState(false);
  const [loaderComplete, setLoaderComplete] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Field grouping for phonematch steps
  const getPhoneMatchFieldGroups = () => {
    const nameFields = config.fields.filter((field) => field.group === "name");
    const phoneDobFields = config.fields.filter((field) => field.group === "phone_dob");
    const addressFields = config.fields.filter((field) => field.group === "address");

    return { nameFields, phoneDobFields, addressFields };
  };

  const { nameFields, phoneDobFields, addressFields } = getPhoneMatchFieldGroups();
  const hasAddressStep = addressFields.length > 0;

  const getCurrentPhoneMatchFields = () => {
    if (isOtpInput) return [];
    switch (phoneMatchStep) {
      case 0: return nameFields;
      case 1: return phoneDobFields;
      case 2: return addressFields;
      default: return nameFields;
    }
  };

  const getPhoneMatchStepTitle = () => {
    if (isOtpInput) return "Enter verification code";
    switch (phoneMatchStep) {
      case 0: return "Name details";
      case 1: return "Phone and date of birth";
      case 2: return "Address details";
      default: return "Name details";
    }
  };

  const isPhoneMatchStepValid = () => {
    if (isOtpInput) {
      return form.values.pin && form.values.pin.length === 6;
    }

    const currentFields = getCurrentPhoneMatchFields();
    return currentFields.every((field) => {
      if (field.type === "address") {
        const addressValue = {
          addr: form.values.addr || "",
          city: form.values.city || "",
          state: form.values.state || "",
          zip: form.values.zip || "",
        };
        const validationResult = field.validation ? field.validation(addressValue) : null;
        return validationResult === null;
      } else {
        const value = form.values[field.name] || "";
        const validationResult = field.validation ? field.validation(value) : null;
        return validationResult === null;
      }
    });
  };

  // Status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      setProcessingVerification(false);
      return true;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setProcessingVerification(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        try {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        } catch (err) {
          // Silent fail
        }
      }

      setLoading(true);
      return true;
    }

    return true;
  };

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
  }, [showIntro]);

  // Reset to first step on reattempt
  useEffect(() => {
    if (attemptsLeft < reAttempts && hasReceivedFailedResponse) {
      setPhoneMatchStep(0);
      setIsOtpInput(false);
      setTemplate({});
      form.clearErrors();
      setHasReceivedFailedResponse(false);
    }
  }, [attemptsLeft, reAttempts, hasReceivedFailedResponse]);

  // Status check hooks
  useStatusCheck(result, false, componentKey, handleStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, { pin: "" }),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};

      if (isOtpInput) {
        if (values.pin.length < 6) {
          errors.pin = text?.phonematch?.code?.error || "Code is required";
        }
        return errors;
      }

      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    // Update data with form values
    Object.keys(form.values).forEach((key) => {
      if (key !== "pin") {
        data[key] = form.values[key];
      }
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;
    data.isOtpInput = isOtpInput;

    if (isOtpInput) {
      template.codematch.key = form.values.pin;
      data.phonematch_continue = template;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((responseData) => {
          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          // Handle PhoneMatch specific responses
          if (responseData.result.action === "PENDING" && !isOtpInput) {
            setLoading(false);
            setTemplate(responseData.continuations.outofband.template);
            setIsOtpInput(true);
            setIsVerifying(false);
            return;
          } else if (responseData.result.action === "PASS") {
            try {
              dispatch({ type: "SET_STATUS", payload: "PASS" });
              dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            } catch (err) {
              // Silent fail
            }
            setLoading(true);
            setProcessingVerification(false);
            return;
          } else {
            // Handle failure
            if (!hasHandledFailure) {
              setHasHandledFailure(true);
              try {
                dispatch({ type: "SET_STATUS", payload: "FAIL" });
                dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
              } catch (err) {
                // Silent fail
              }
            }
            setLoading(true);
            return;
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setProcessingVerification(true);
            setLoaderComplete(false);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);
          setHasShownFormForCurrentAttempt(false);
          waitingForConfirmationChangeRef.current = true;
          setResult(true);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  const handleDobChange = (dob) => {
    const formattedDob = `${dob.year}${dob.month}${dob.day}`;
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  const getNewPin = () => {
    setIsOtpInput(false);
    // Go back to the last step before OTP
    setPhoneMatchStep(hasAddressStep ? 2 : 1);
    setTemplate({});
    setResult(false);
    form.setFieldValue("pin", "");
  };

  const getButtonType = () => {
    const maxStep = hasAddressStep ? 2 : 1;
    return phoneMatchStep >= maxStep ? "submit" : "button";
  };

  const getButtonText = () => {
    const maxStep = hasAddressStep ? 2 : 1;
    return phoneMatchStep >= maxStep ? buttonText : "Continue";
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);
    setLoading(false);
    form.clearErrors();
  }, [componentKey]);

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  if (loading && !isVerifying) {
    if (processingVerification && isEnglish) {
      const verificationMessages = [
        "Verifying phone number...",
        "Checking personal information...",
        "Validating details...",
        "Processing verification request...",
        "Finalizing phone verification...",
      ];

      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, display: "flex" }}>
            <EnglishLoadingWithCompletion
              messages={verificationMessages}
              finalMessageDuration={4000}
              alwaysAnimateFinalStep={true}
              onComplete={() => {
                console.log("✅ PhoneMatch verification loader animation complete");
                setLoaderComplete(true);
              }}
            />
          </Box>
        </Box>
      );
    }
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {!showIntro && !isOtpInput && (
          <Box
            ref={topBarRef}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 5,
              width: "100%",
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              borderBottom: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[3]
              }`,
              padding: "4px 0",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {getPhoneMatchStepTitle()}
            </Text>
          </Box>
        )}

        <Box
          style={{
            marginTop: !showIntro && !isOtpInput ? topBarHeight : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
            />
          )}

          {!showIntro && !isOtpInput && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Stack spacing="md" style={{ flexGrow: 1, width: "100%" }}>
                {attemptsLeft < reAttempts && !isVerifying && (
                  <Alert
                    mt="xl"
                    mb="md"
                    title="Verification failed"
                    styles={{
                      root: {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[9]
                            : theme.colors.red[0],
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[0]
                            : theme.colors.red[9],
                      },
                      title: {
                        fontSize: "md",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                      message: {
                        fontSize: "sm",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                    }}
                  >
                    Please check that you've entered your correct information and try again.
                  </Alert>
                )}

                {/* Current step fields */}
                <div>
                  {/* Name fields (step 0) */}
                  {phoneMatchStep === 0 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {nameFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Phone and DOB fields (step 1) */}
                  {phoneMatchStep === 1 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {phoneDobFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Address fields (step 2) */}
                  {phoneMatchStep === 2 && hasAddressStep && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {addressFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}
                </div>

                {/* Back button for step 1 */}
                {phoneMatchStep > 0 && (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setPhoneMatchStep(phoneMatchStep - 1)}
                      c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.gray[6]}
                      sx={{
                        fontSize: "13px",
                        height: "32px",
                        padding: "0 16px",
                        "&:hover": {
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[5]
                              : theme.colors.gray[1],
                        },
                      }}
                    >
                      ← Back
                    </Button>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box mt="md" pb="15px" mb="80px" style={{ width: "100%" }}>
                <Button
                  mt="xl"
                  fullWidth
                  size="lg"
                  type={getButtonType()}
                  disabled={isVerifying || !isPhoneMatchStepValid()}
                  loading={false}
                  onClick={(e) => {
                    const maxStep = hasAddressStep ? 2 : 1;
                    if (phoneMatchStep < maxStep) {
                      e.preventDefault();
                      setPhoneMatchStep(phoneMatchStep + 1);
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Sending Data</Text>
                    </MantineGroup>
                  ) : (
                    getButtonText()
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* OTP Input Screen */}
          {!showIntro && isOtpInput && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
                animation: `slideUpBounce 0.5s ${bounceTransition}`
              }}
            >
              <Box style={{ flexGrow: 1 }}>
                <Center mt={inputSize === "md" ? 25 : 10}>
                  <Stack align="center">
                    <ThemeIcon
                      variant="light"
                      c="teal"
                      size={inputSize === "md" ? 70 : 65}
                      radius={"xl"}
                    >
                      <IconShieldCheck
                        stroke={1}
                        size={inputSize === "md" ? 50 : 45}
                        color="teal"
                      />
                    </ThemeIcon>

                    <Text fz={inputSize === "md" ? 30 : 23} fw={700}>
                      {text?.phonematch?.code?.title || "Enter verification code"}
                    </Text>
                    <Text
                      ta="center"
                      c="dimmed"
                      fz={inputSize === "md" ? 24 : 14}
                    >
                      {text?.phonematch?.code?.subTitle || "We sent a code to your phone"}
                    </Text>
                  </Stack>
                </Center>

                <Group position="center" mt={40}>
                  <FocusTrap>
                    <PinInput
                      data-autofocus
                      type="number"
                      oneTimeCode
                      size={"xl"}
                      length={6}
                      {...form.getInputProps("pin")}
                    />
                  </FocusTrap>
                </Group>
                <Stack align="center" mt={40} spacing={0}>
                  <Text fz="xs" c="dimmed">
                    {text?.phonematch?.code?.newCodeNote || "Didn't receive a code?"}
                  </Text>
                  <Button onClick={getNewPin} variant="subtle">
                    {text?.phonematch?.code?.newCodeButton || "Get new code"}
                  </Button>
                </Stack>
              </Box>

              <Box mt="md" pb="15px" mb="80px" style={{ width: "100%" }}>
                <Button
                  mt="xl"
                  fullWidth
                  size="lg"
                  type="submit"
                  disabled={isVerifying || !isPhoneMatchStepValid()}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Verifying</Text>
                    </MantineGroup>
                  ) : (
                    text?.phonematch?.code?.button || "Verify Code"
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default PhoneMatchForm;